// src/components/Layout/Sidebar.jsx
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider,
  Avatar,
  Chip,
  Button,
} from '@mui/material';
import {
  Movie,
  Tv,
  Search,
  RssFeed,
  Chat,
  Person,
  Bookmark,
  TrendingUp,
  Theaters,
  Star,
  Settings,
  Visibility,
  PlaylistPlay,
  Flag,
  AdminPanelSettings,
  Whatshot,
  EmojiEvents,
  Info,
  SupportAgent,
  Block,
  Article,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { isAdmin } from '../../utils/adminUtils';
import { showAuthAlert } from '../../utils/authAlerts';
import S3Avatar from '../Common/S3Avatar';
import { WatchlistSidebar } from '../Watchlist';
import { HypeCircleLogo } from '../Branding';
import { useTheme, useMediaQuery } from '@mui/material';

const Sidebar = ({ open, onClose, variant = 'temporary' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addNotification } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const mainNavItems = [
    // Hide Movies and Shows on mobile as they're in navbar
    
      { label: 'Movies', icon: <Movie />, path: '/movies' },
      { label: 'Shows', icon: <Tv />, path: '/shows' },
      { label: 'News', icon: <Article />, path: '/news' },
    
    // Hide Feed on mobile as it's in navbar for authenticated users
    // ...(!isMobile || !user ? [
    //   { label: 'Feed', icon: <RssFeed />, path: '/feed', requiresAuth: true, feature: 'FEED' },
    // ] : []),
    { label: 'Leaderboards', icon: <TrendingUp />, path: '/leaderboards' },
    { label: 'Achievements', icon: <EmojiEvents />, path: '/achievements', requiresAuth: true, feature: 'ACHIEVEMENTS' },
    
  ];

  const personalNavItems = [
    { label: 'Watched Movies', icon: <Visibility />, path: '/my-watched', requiresAuth: true, feature: 'WATCHLIST' },
    { label: 'Bookmarks', icon: <Bookmark />, path: '/bookmarks', requiresAuth: true, feature: 'BOOKMARKS' },
    { label: 'News Bookmarks', icon: <Article />, path: '/news/bookmarks', requiresAuth: true, feature: 'NEWS_BOOKMARKS' },
    { label: 'My Reports', icon: <Flag />, path: '/my-reports', requiresAuth: true, feature: 'REPORT' },
    { label: 'Blocked Users', icon: <Block />, path: '/blocked-users', requiresAuth: true, feature: 'BLOCK_USER', highlighted: true },
  ];

  const infoNavItems = [
    { label: 'About Us', icon: <Info />, path: '/about' },
    { label: 'Support Center', icon: <SupportAgent />, path: '/support' },
  ];

  const bottomNavItems = [
    // { label: 'Settings', icon: <Settings />, path: '/settings', requiresAuth: true, feature: 'PROFILE' },
  ];

  const adminNavItems = [
    { label: 'Control Center', icon: <AdminPanelSettings />, path: '/admin/control-center', key: 'admin-control-center', featured: true },
    { label: 'Admin Dashboard', icon: <AdminPanelSettings />, path: '/admin', key: 'admin-dashboard' },
    { label: 'Content Manager', icon: <Movie />, path: '/admin/content', key: 'admin-content' },
    { label: 'News Manager', icon: <Article />, path: '/admin/news', key: 'admin-news' },
    { label: 'Support Tickets', icon: <SupportAgent />, path: '/admin/support/tickets', key: 'admin-support-tickets' },
    { label: 'Achievement Test', icon: <EmojiEvents />, path: '/admin/achievement-test', key: 'admin-achievement-test' },
    { label: 'Post Reports', icon: <Flag />, path: '/admin/reports', key: 'admin-reports' },
    { label: 'Content Reports', icon: <Movie />, path: '/admin/content-reports', key: 'admin-content-reports' },
    { label: 'Leaderboard Data', icon: <EmojiEvents />, path: '/admin/leaderboard-data', key: 'admin-leaderboard-data' },
  ];

  const handleNavigation = (path, requiresAuth = false, feature = 'DEFAULT') => {
    if (requiresAuth && !user) {
      showAuthAlert(addNotification, navigate, feature);
      if (variant === 'temporary') {
        onClose();
      }
      return;
    }
    navigate(path);
    if (variant === 'temporary') {
      onClose();
    }
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const drawerContent = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* User Profile Section */}
      {user ? (
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <S3Avatar 
              s3Key={user?.avatar} 
              sx={{ width: 48, height: 48 }}
            >
              {user?.displayName?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || <Person />}
            </S3Avatar>
            <Box flexGrow={1}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                noWrap
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
                onClick={() => handleNavigation('/profile')}
              >
                {user?.displayName || user?.username || 'User'}
              </Typography>
              {(() => {
                // Get active title from titleOrder or titles array (same logic as UserProfile)
                let activeTitle = null;
                if (user?.titleOrder && user.titleOrder.length > 0) {
                  activeTitle = user.titleOrder[0];
                } else if (user?.titles && user.titles.length > 0) {
                  activeTitle = user.titles[0];
                }
                
                return activeTitle ? (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: 'primary.main',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      '&:hover::after': {
                        content: '" (Achievement)"',
                        fontSize: '0.6rem',
                        fontWeight: 400,
                        color: 'text.secondary',
                        textTransform: 'none',
                      }
                    }}
                    noWrap
                  >
                    {activeTitle}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    Movie Enthusiast
                  </Typography>
                );
              })()}
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box display="flex" gap={2} mt={2}>
            <Button
              size="small"
              variant="text"
              onClick={() => handleNavigation('/profile')}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
            >
              <Typography variant="caption">
                <strong>{user?.followingCount || 0}</strong> Following
              </Typography>
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => handleNavigation('/profile')}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
            >
              <Typography variant="caption">
                <strong>{user?.followersCount || 0}</strong> Followers
              </Typography>
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HypeCircleLogo size="md" variant="default" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Join thousands of movie lovers sharing their passion
          </Typography>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={() => {
              navigate('/auth?mode=signup');
              if (variant === 'temporary') onClose();
            }}
            sx={{ mb: 1 }}
          >
            Sign Up Free
          </Button>
          <Button 
            variant="text" 
            size="small"
            onClick={() => {
              navigate('/auth?mode=signin');
              if (variant === 'temporary') onClose();
            }}
          >
            Already have an account?
          </Button>
        </Box>
      )}

      {/* Main Navigation */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
        },
      }}>
        <List sx={{ py: 1 }}>
          {mainNavItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path, item.requiresAuth, item.feature)}
                selected={isActive(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  py: item.featured ? 1.5 : 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                    minWidth: item.featured ? 48 : 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 700 : (item.featured ? 600 : 400),
                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                    fontSize: item.featured ? '1.1rem' : '1rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mx: 2, my: 1 }} />

        {/* Personal Section */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            My Lists
          </Typography>
        </Box>
        
        <List sx={{ py: 0 }}>
          {/* Watchlists Section */}
          <WatchlistSidebar onClose={variant === 'temporary' ? onClose : undefined} />
          
          {personalNavItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path, item.requiresAuth, item.feature)}
                selected={isActive(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive(item.path) ? 'primary.main' : (item.highlighted ? 'warning.main' : 'inherit'),
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : (item.highlighted ? 500 : 400),
                    color: isActive(item.path) ? 'primary.main' : (item.highlighted ? 'warning.main' : 'inherit'),
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mx: 2, my: 1 }} />

        {/* Info Section */}
        <List sx={{ py: 0 }}>
          {infoNavItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActive(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                    fontSize: '0.9rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        {/* Admin Section */}
        {isAdmin(user) && (
          <Box>
            <Divider sx={{ mx: 2, my: 1 }} />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Admin
              </Typography>
            </Box>
            <List sx={{ py: 0 }}>
              {adminNavItems.map((item) => (
                <ListItem key={item.key} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      if (variant === 'temporary') onClose();
                    }}
                    selected={isActive(item.path)}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      py: item.featured ? 1.5 : 1,
                      '&.Mui-selected': {
                        bgcolor: 'primary.50',
                        '&:hover': {
                          bgcolor: 'primary.100',
                        },
                      },
                      ...(item.featured && {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      }),
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        color: item.featured ? 'primary.contrastText' : (isActive(item.path) ? 'primary.main' : 'inherit'),
                        minWidth: item.featured ? 48 : 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: item.featured ? 700 : (isActive(item.path) ? 600 : 400),
                        color: item.featured ? 'primary.contrastText' : (isActive(item.path) ? 'primary.main' : 'inherit'),
                        fontSize: item.featured ? '1.1rem' : '1rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>

      {/* Bottom Navigation */}
      <Box sx={{ flexShrink: 0 }}>
        {bottomNavItems.length > 0 && (
          <>
            <Divider sx={{ mx: 2 }} />
            <List sx={{ py: 1 }}>
              {bottomNavItems.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path, item.requiresAuth, item.feature)}
                    selected={isActive(item.path)}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      '&.Mui-selected': {
                        bgcolor: 'primary.50',
                        '&:hover': {
                          bgcolor: 'primary.100',
                        },
                      },
                    }}
                  >
                    {(user || !isMobile) && (
                      <ListItemIcon 
                        sx={{ 
                          color: isActive(item.path) ? 'primary.main' : 'inherit',
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive(item.path) ? 600 : 400,
                        color: isActive(item.path) ? 'primary.main' : 'inherit',
                        fontSize: '0.9rem',
                      }}
                      sx={(!user && isMobile) ? { pl: 2 } : {}}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {/* App Branding */}
        <Box sx={{ 
          px: 2, 
          py: 2, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <HypeCircleLogo size="sm" variant="text" />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
            Version 1.0.1
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: {
          border: 'none',
          boxShadow: variant === 'permanent' ? 'none' : 3,
          width: 280,
          height: '100%',
          ...(variant === 'persistent' && {
            position: 'fixed',
            height: 'calc(100vh - 64px)',
            top: 64,
            zIndex: 1200,
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;


